import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/site/RichTextEditor";
import SEOFields from "@/components/admin/SEOFields";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = { title: "", excerpt: "", content: "", cover_image: "", author: "PharmacoHires Team", tags: "", is_published: true, meta_title: "", meta_description: "", meta_keywords: "", og_image: "" };

export default function AdminBlogEditor() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    api.get("/admin/blog").then((r) => {
      const post = (r.data || []).find((p) => p.id === id);
      if (post) setForm({ ...empty, ...post, tags: (post.tags || []).join(", ") });
    }).finally(() => setLoading(false));
  }, [editing, id]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: (form.tags || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.patch(`/admin/blog/${id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(editing ? "Updated" : "Created");
      nav("/admin/blog");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <Link to="/admin/blog" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary"><ChevronLeft className="h-4 w-4" /> All articles</Link>
      <h1 className="font-heading text-2xl font-semibold text-slate-900">{editing ? "Edit article" : "New article"}</h1>
      <form onSubmit={save} className="mt-6 space-y-5 rounded-xl border border-slate-200 bg-white p-6" data-testid="blog-editor">
        <div><Label>Title *</Label><Input required value={form.title} onChange={onChange("title")} data-testid="blog-form-title" /></div>
        <div><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={onChange("excerpt")} /></div>
        <div><Label>Cover image URL</Label><Input value={form.cover_image} onChange={onChange("cover_image")} placeholder="https://..." /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Author</Label><Input value={form.author} onChange={onChange("author")} /></div>
          <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={onChange("tags")} placeholder="interview, salary, career" /></div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="blog-form-published" />
          <span className="text-sm text-slate-600">{form.is_published ? "Published" : "Draft"}</span>
        </div>
        <div><Label>Content *</Label>
          <RichTextEditor
            value={form.content}
            onChange={(html) => setForm({ ...form, content: html })}
            placeholder="Write your article…"
            testId="blog-editor-content"
          />
        </div>
        <SEOFields
          values={{ meta_title: form.meta_title, meta_description: form.meta_description, meta_keywords: form.meta_keywords, og_image: form.og_image }}
          onChange={(v) => setForm({ ...form, ...v })}
          titlePlaceholder="Optional — defaults to the article title"
          descriptionPlaceholder="Optional — defaults to the excerpt"
          testIdPrefix="blog-seo"
        />
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Link to="/admin/blog"><Button type="button" variant="outline">Cancel</Button></Link>
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="blog-form-save"><Save className="mr-1 h-4 w-4" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
